import axios from "axios";
import { getAuth } from "firebase/auth";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;;
const API_BASE_URL_EU = process.env.REACT_APP_API_BASE_URL_EU;
// Axios instance for API requests
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
  withCredentials: false,
});

const apiClientEU = axios.create({
  baseURL: API_BASE_URL_EU,
  headers: { "Content-Type": "application/json" },
  withCredentials: false,
});

// Helper function to get the Firebase ID token
const getIdToken = async () => {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error("User is not authenticated");
  }

  return await user.getIdToken();
};

// Fetch-based POST request
export const postRequest = async (endpoint, body) => {
  try {
    const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
      mode: "cors",
    });

    if (!response.ok) {
      // defaults
      let errorMessage = "Request failed";
      let errorCode = "";

      // try parsing JSON to extract server message + code
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
        errorCode    = errorData.code    || "";
      } catch {
        // not JSON or parse failed, leave defaults
      }

      // throw an Error with a .code property attached
      const err = new Error(errorMessage);
      err.code = errorCode;
      throw err;
    }

    return await response.json();
  } catch (error) {
    console.error("API Error:", error.message, error.code || "");
    throw error;
  }
};

export const fetchUserData = async (userId) => {
  try {
    const idToken = await getIdToken(); // Retrieve the Firebase ID token

    const response = await apiClient.post(
      "/authEditorFunctions-fetchUserData", // Endpoint for fetching user data
      { userId }, // Pass the user ID as the request body
      {
        headers: { Authorization: `Bearer ${idToken}` }, // Include the ID token in the headers
      }
    );

    // Ensure the response contains valid user data
    if (!response.data || !response.data.user) {
      throw new Error("User not found or invalid response from server");
    }

    return response.data.user; // Return user data object
  } catch (error) {
    // Handle and log errors
    console.error("Error fetching user data:", error.response?.data || error.message);
    throw error.response?.data?.error || error.message;
  }
};


// Functions to interact with the Cloud Functions

export const fetchUserStories = async () => {
  
  try {
    const idToken = await getIdToken();
    const response = await apiClient.post("/userEditorFunctions-fetchUserStories", {}, {
      headers: { Authorization: `Bearer ${idToken}` },
    });

    // Ensure the response contains an array of stories
    return Array.isArray(response.data.stories) ? response.data.stories : [];
  } catch (error) {
    console.error("Error fetching user stories:", error.response?.data || error.message);
    throw error.response?.data?.error || error.message;
  }
};

export const fetchStoriesForEditor = async ({
  isUGC, 
  isFlagged, // Renamed to match backend
  writerId, // Renamed from userID
  tags, // Renamed from category
  isPublished, // Renamed from published
  isActive, // New parameter from backend
  limit = 10, 
  sortBy, // Changed default sort field to match backend
  order
}) => {
  try {
    const idToken = await getIdToken();

    // Construct request payload
    const requestBody = {
      isUGC,
      isFlagged,
      writerId,
      tags,
      isPublished,
      isActive,
      limit,
      sortBy,
      order,
    };

    // Remove null/undefined values to avoid sending empty fields
    Object.keys(requestBody).forEach(
      (key) => requestBody[key] === undefined && delete requestBody[key]
    );

    const response = await apiClient.post(
      "/editorialEditorFunctions-fetchStoriesForEditor",
      requestBody,
      {
        headers: { Authorization: `Bearer ${idToken}` },
      }
    );

    return Array.isArray(response.data.stories) ? response.data.stories : [];
  } catch (error) {
    console.error("Error fetching stories for editor:", error.response?.data || error.message);
    throw error.response?.data?.error || error.message;
  }
};

export const fetchDraftStoriesForEditor = async ({
  writerId,
  tags,
  limit = 1000,
  sortBy,
  order,
}) => {
  try {
    const idToken = await getIdToken();

    const requestBody = {
      writerId,
      tags,
      limit,
      sortBy,
      order,
    };

    // Remove undefined or null values
    Object.keys(requestBody).forEach(
      (key) => requestBody[key] === undefined && delete requestBody[key]
    );

    const response = await apiClient.post(
      "/editorialEditorFunctions-fetchDraftStoriesForEditor",
      requestBody,
      {
        headers: { Authorization: `Bearer ${idToken}` },
      }
    );

    return Array.isArray(response.data.drafts) ? response.data.drafts : [];
  } catch (error) {
    console.error("Error fetching draft stories for editor:", error.response?.data || error.message);
    throw error.response?.data?.error || error.message;
  }
};



export const fetchStoryDetails = async (storyID) => {
  try {
    const idToken = await getIdToken();
    const response = await apiClient.post(
      "/userEditorFunctions-getStoryDetails",
      { storyID },
      {
        headers: { Authorization: `Bearer ${idToken}` },
      }
    );

    // Ensure the cover image URL is available and correctly formatted
    const story = response.data.story;
    if (!story.coverImageUrl && story.coverImagePath) {
      // Use a placeholder if no coverImageUrl was provided
      story.coverImageUrl = "https://via.placeholder.com/150";
    }

    return story;
  } catch (error) {
    console.error(`Error fetching story details for ${storyID}:`, error.response?.data || error.message);
    throw error.response?.data?.error || error.message;
  }
};

export const fetchStoryDetailsEditorial = async (storyID) => {
  try {
    const idToken = await getIdToken();
    const response = await apiClient.post(
      "/editorialEditorFunctions-getStoryDetailsEditorial",
      { storyID },
      {
        headers: { Authorization: `Bearer ${idToken}` },
      }
    );

    // Validate response structure
    if (!response.data || !response.data.story) {
      throw new Error("Invalid response: Missing story data.");
    }

    let story = response.data.story;

    // Ensure the cover image URL is properly set
    if (!story.coverImageUrl && story.coverUrl) {
      story.coverImageUrl = story.coverUrl; // Use existing coverUrl if present
    }

    if (!story.coverImageUrl) {
      story.coverImageUrl = "https://fakeimg.pl/140x200?text=upload+a+cover&font=noto&font_size=16"; // Use placeholder if missing
    }

    // Ensure chapters exist as an empty object if missing
    if (!story.chapters) {
      story.chapters = {};
    }

    return story;
  } catch (error) {
    console.error(`Error fetching story details (editorial) for ${storyID}:`, error.response?.data || error.message);
    throw error.response?.data?.error || error.message;
  }
};


export const saveChapterEditorial = async ({ storyID, chapterIndex, chapterName, contentBuffer, paragraphCount }) => {
  try {
    const idToken = await getIdToken();
    const response = await apiClient.post("/editorialEditorFunctions-createOrUpdateChapterEditorial", {
      storyID,
      chapterIndex,
      chapterName, // Pass chapter title correctly
      contentBuffer,
      paragraphCount, // Include paragraph count
    }, {
      headers: { Authorization: `Bearer ${idToken}` },
    });

    return response.data;
  } catch (error) {
    console.error(`Error saving chapter ${chapterIndex} for story ${storyID}:`, error.response?.data || error.message);
    throw error.response?.data?.error || error.message;
  }
};

export const togglePublishChapter = async (storyID, chapterIndex, isPublished) => {
  try {
    const idToken = await getIdToken();
    const response = await apiClient.post("/editorialEditorFunctions-togglePublishChapter", {
      storyID,
      chapterIndex,
      isPublished,
    }, {
      headers: { Authorization: `Bearer ${idToken}` },
    });

    return response.data;
  } catch (error) {
    console.error(`Error toggling publish status for chapter ${chapterIndex} in story ${storyID}:`, error.response?.data || error.message);
    throw error.response?.data?.error || error.message;
  }
};

// Fetch chapter content
export const getChapterContent = async (contentUrl) => {
  try {
    const idToken = await getIdToken(); // Retrieve the Firebase ID token
    const response = await apiClient.post(
      "/userEditorFunctions-getChapterContent", // Endpoint for fetching chapter content
      { contentUrl }, // Pass the content URL as the request body
      {
        headers: { Authorization: `Bearer ${idToken}` }, // Include the ID token in the headers
      }
    );

    // Check if the response contains valid content
    if (!response.data || !response.data.content) {
      throw new Error("No content returned from the server");
    }

    return response.data.content; // Return the plain text content
  } catch (error) {
    if (error.response) {
      const status = error.response.status;
      const errorMessage = error.response.data?.message || "Unknown error";

      if (status === 404) {
        console.warn(`⚠️ Chapter not found: ${errorMessage}`);
        return null; // Return `null` explicitly for missing chapters
      } else if (status === 500) {
        console.error(`🚨 Server error (500): ${errorMessage}`);
      } else {
        console.error(`Error fetching chapter content: ${status} - ${errorMessage}`);
      }

      throw new Error(errorMessage);
    } else {
      console.error("Network or unknown error:", error.message);
      throw new Error("Failed to connect to the server");
    }
  }
};


// Fetch chapter content for editors, including chapter name
export const getChapterContentEditorial = async ({ storyID, chapterIndex }) => {
  try {
    const idToken = await getIdToken(); // Retrieve the Firebase ID token

    const response = await apiClient.post(
      "/editorialEditorFunctions-getChapterContentEditorial", // Endpoint for editorial chapter content
      { storyID, chapterIndex }, // Pass story ID and chapter index as request body
      {
        headers: { Authorization: `Bearer ${idToken}` }, // Include the ID token in the headers
      }
    );

    // Check if the response contains valid content
    if (!response.data || !response.data.content || !response.data.chapterName) {
      throw new Error("Invalid response: Missing content or chapter name from the server");
    }

    return {
      chapterName: response.data.chapterName, // Include chapter name in the response
      content: response.data.content, // Include the plain text content
    };
  } catch (error) {
    // Handle and log errors
    console.error(
      `Error fetching editorial chapter content (Story: ${storyID}, Chapter: ${chapterIndex}):`,
      error.response?.data || error.message
    );
    throw error.response?.data?.error || error.message;
  }
};



export const submitChapterForReview = async (storyID, chapterIndex, contentUrl) => {
  try {
    const idToken = await getIdToken();
    const response = await apiClient.post(
      "/userEditorFunctions-submitChapterForReview",
      { storyID, chapterIndex, contentUrl },
      {
        headers: { Authorization: `Bearer ${idToken}` },
      }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || "Submission failed.");
    }

    return response.data;
  } catch (error) {
    console.error("Error submitting chapter for review:", error.response?.data || error.message);
    throw error;
  }
};

export const createStory = async ({ title, description, cover, categories }) => {
  try {
    const idToken = await getIdToken();

    const formData = { title, description, categories };

    // Convert cover image to base64
    if (cover) {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result;
          if (result.includes(",")) {
            resolve(result.split(",")[1]); // Extract only base64 content
          } else {
            reject(new Error("Invalid base64 format."));
          }
        };
        reader.onerror = () => reject(new Error("Failed to read cover image file."));
        reader.readAsDataURL(cover);
      });

      const fileNameParts = cover.name.split(".");
      const coverImageExtension = fileNameParts.length > 1 ? fileNameParts.pop().toLowerCase() : "";

      if (!coverImageExtension) {
        throw new Error("Invalid cover image format.");
      }

      formData.coverImageBuffer = base64;
      formData.coverImageExtension = coverImageExtension;
    }
    // Send request to backend (includes moderation)
    const response = await apiClient.post("/userEditorFunctions-createStory", formData, {
      headers: { Authorization: `Bearer ${idToken}` },
    });

    return response.data.storyID;
  } catch (error) {
    console.error("Error creating story:", error.response?.data || error.message);

    // Capture and return the error message from the backend (400 errors)
    if (error.response?.status === 400) {
      throw new Error(error.response.data.message || "Moderation failed. Please check your content.");
    }

    throw new Error(error.response?.data?.message || error.message);
  }
};

export const editStory = async ({ storyID, title, description, cover, categories }) => {
  try {
    const idToken = await getIdToken();
    const formData = { storyID, title, description, categories };

    // If a new cover image is provided, process it into a base64 string.
    if (cover) {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result;
          if (result.includes(",")) {
            resolve(result.split(",")[1]); // Extract only base64 content
          } else {
            reject(new Error("Invalid base64 format."));
          }
        };
        reader.onerror = () => reject(new Error("Failed to read cover image file."));
        reader.readAsDataURL(cover);
      });

      const fileNameParts = cover.name.split(".");
      const coverImageExtension = fileNameParts.length > 1 ? fileNameParts.pop().toLowerCase() : "";

      if (!coverImageExtension) {
        throw new Error("Invalid cover image format.");
      }

      formData.coverImageBuffer = base64;
      formData.coverImageExtension = coverImageExtension;
    }

    // Send request to backend edit endpoint
    const response = await apiClient.post("/userEditorFunctions-editStory", formData, {
      headers: { Authorization: `Bearer ${idToken}` },
    });

    return response.data;
  } catch (error) {
    console.error("Error editing story:", error.response?.data || error.message);

    if (error.response?.status === 400) {
      throw new Error(error.response.data.message || "Edit story failed. Please check your content.");
    }

    throw new Error(error.response?.data?.message || error.message);
  }
};

export const togglePublishStory = async (storyID) => {
  try {
    const idToken = await getIdToken();
    const response = await apiClient.post(
      "/userEditorFunctions-togglePublishStory", // Endpoint name must match your Cloud Function export name
      { storyID },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
    return response.data;
  } catch (error) {
    console.error(
      `Error toggling publish status for story ${storyID}:`,
      error.response?.data || error.message
    );
    throw error.response?.data?.error || error.message;
  }
};

export const toggleActiveStatus = async (storyID, isActive) => {  
  try {
    const idToken = await getIdToken();
    const response = await apiClient.post(
      "/userEditorFunctions-toggleActiveStatus",
      { storyID, isActive },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );

    return response.data;
  } catch (error) {
    console.error(
      `Error toggling active status for story ${storyID}:`,
      error.response?.data || error.message
    );
    throw error.response?.data?.error || error.message;
  }
};

export const deleteStory = async (storyID) => {
  try {
    const idToken = await getIdToken();
    const response = await apiClient.post(
      "/userEditorFunctions-deleteStory",  // This should match your Cloud Function's endpoint name
      { storyID },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
    return response.data;
  } catch (error) {
    console.error(`Error deleting story ${storyID}:`, error.response?.data || error.message);
    throw error.response?.data?.error || error.message;
  }
};



export const createStoryEditorial = async ({ title, description, cover, categories, authorId, authorName }) => {
  try {
    const idToken = await getIdToken();

    // Ensure required fields are present
    if (!title || !description || !authorId || !authorName) {
      throw new Error("Missing required fields: title, description, authorId, or authorName.");
    }

    // Prepare formData for backend
    const formData = {
      title,
      description,
      writerId: authorId, // Ensure correct field name
      writerName: authorName, // Include the writer's name
      tags: Array.isArray(categories) ? categories : [], // Ensure tags is always an array
    };

    // Handle cover image processing (only if a file is provided)
    if (cover instanceof File) {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve(reader.result.split(",")[1]); // Extract base64 content
        };
        reader.onerror = () => {
          reject(new Error("Failed to read cover image file."));
        };
        reader.readAsDataURL(cover); // Read as Base64
      });

      formData.coverImageBuffer = base64;
      formData.coverImageExtension = cover.name.split(".").pop(); // Extract file extension
    }

    // Send request to editorial-specific backend function
    const response = await apiClient.post("/editorialEditorFunctions-createStoryEditorial", formData, {
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": "application/json",
      },
    });

    // Ensure response contains a valid storyID
    if (!response.data || !response.data.storyID) {
      throw new Error("Unexpected response from the server.");
    }

    return response.data.storyID;
  } catch (error) {
    console.error("Error creating story (editorial):", error.response?.data || error.message);
    throw error.response?.data?.error || error.message;
  }
};


// Save or update a chapter
export const saveChapter = async ({ storyID, chapterIndex, title, contentBuffer, autosave = false, }) => {
  try {
    const idToken = await getIdToken();
    const response = await apiClient.post("/userEditorFunctions-updateChapter", {
      storyID,
      chapterIndex,
      title,
      contentBuffer,
      ...(autosave && { autosave: true }),
    }, {
      headers: { Authorization: `Bearer ${idToken}` },
    });
    return response.data;
  } catch (error) {
    console.error(`Error saving chapter ${chapterIndex} for story ${storyID}:`, error.response?.data || error.message);
    throw error.response?.data?.error || error.message;
  }
};

// Delete a chapter
export const deleteChapter = async (storyID, chapterIndex) => {
  try {
    const idToken = await getIdToken();
    const response = await apiClient.post("/userEditorFunctions-deleteChapter", { storyID, chapterIndex }, {
      headers: { Authorization: `Bearer ${idToken}` },
    });
    return response.data;
  } catch (error) {
    console.error(`Error deleting chapter ${chapterIndex} for story ${storyID}:`, error.response?.data || error.message);
    throw error.response?.data?.error || error.message;
  }
};

// Edit story metadata
export const editStoryMetadata = async ({ storyId, title, description, coverImage }) => {
  try {
    const idToken = await getIdToken();
    const response = await apiClient.post("/userEditorFunctions-editStoryMetadata", {
      storyId,
      title,
      description,
      coverImage,
    }, {
      headers: { Authorization: `Bearer ${idToken}` },
    });
    return response.data;
  } catch (error) {
    console.error(`Error editing metadata for story ${storyId}:`, error.response?.data || error.message);
    throw error.response?.data?.error || error.message;
  }
};

export const publishChapter = async (draftStoryId, chapterNumber) => {
  try {
    const idToken = await getIdToken(); // Get Firebase auth token

    const response = await apiClient.post(
      "/userEditorFunctions-publishChapter",
      { draftStoryId, chapterNumber },
      {
        headers: { Authorization: `Bearer ${idToken}` },
      }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to publish chapter.");
    }

    return response.data;
  } catch (error) {
    console.error(
      `Error publishing chapter ${chapterNumber} for story ${draftStoryId}:`,
      error.response?.data || error.message
    );
    // Throw the detailed error message if available, otherwise a generic one.
    throw error.response?.data?.error || error.response?.data?.message || error.message;
  }
};


export const fetchWriters = async () => {
  try {
    const idToken = await getIdToken();
    const response = await apiClient.post(
      "/editorialEditorFunctions-fetchWriters",
      {},
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
    return response.data.writers.map(writer => ({
      id: writer.uid, // Ensure frontend gets UID
      name: writer.Username, // Match expected name field
    }));
  } catch (error) {
    console.error("Error fetching writers:", error.response?.data || error.message);
    throw error;
  }
};

export const delistChapter = async (storyID, chapterIndex, reason) => {
  try {
    const idToken = await getIdToken();
    const response = await apiClient.post(
      "/editorialEditorFunctions-delistChapter",
      { storyID, chapterIndex, reason },
      {
        headers: { Authorization: `Bearer ${idToken}` },
      }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to delist chapter.");
    }

    return response.data;
  } catch (error) {
    console.error(`Error delisting chapter ${chapterIndex} for story ${storyID}:`, error.response?.data || error.message);
    throw error.response?.data?.error || error.message;
  }
};
export const aiEditParagraph = async ({ storyID, chapterIndex, paragraphText, chapterText }) => {
  try {
    const idToken = await getIdToken();
    const response = await apiClient.post(
      "/gptAssistantFunctions-editParagraph",
      { storyID, chapterIndex, paragraphText, chapterText },
      {
        headers: { Authorization: `Bearer ${idToken}` },
      }
    );

    if (!response.data || !response.data.editedText) {
      throw new Error("AI editing failed or returned invalid response.");
    }

    return response.data.editedText;
  } catch (error) {
    console.error("Error AI editing paragraph:", error.response?.data || error.message);
    throw error.response?.data?.error || error.message;
  }
};

export const aiWriteNextParagraph = async ({ storyID, chapterIndex, paragraphText, chapterText }) => {
  try {
    const idToken = await getIdToken();
    const response = await apiClient.post(
      "/gptAssistantFunctions-writeNextParagraph",
      { storyID, chapterIndex, paragraphText, chapterText },
      {
        headers: { Authorization: `Bearer ${idToken}` },
      }
    );

    if (!response.data || !response.data.responseText) {
      throw new Error("AI writing next paragraph failed or returned invalid response.");
    }

    return response.data.responseText;
  } catch (error) {
    console.error("Error AI writing next paragraph:", error.response?.data || error.message);
    throw error.response?.data?.error || error.message;
  }
};

export const saveUsername = async (username) => {
  try {
    const idToken = await getIdToken(); // Use existing helper

    const response = await apiClient.post(
      "/authEditorFunctions-saveUsername", // ✅ Match your Cloud Function name
      { username }, // ✅ Pass the username as body
      {
        headers: { Authorization: `Bearer ${idToken}` }, // Include Firebase ID token
      }
    );

    if (!response.data.success) {
      const error = new Error(response.data.message || "Failed to save username.");
      error.code = response.data.code || "";
      throw error;
    }

    return response.data; // Return success response
  } catch (error) {
    console.error("Error saving username:", error.response?.data || error.message);
    throw error.response?.data?.error || error.message;
  }
};

export const createConversation = async ({ title, storyId, summary }) => {
  try {
    const idToken = await getIdToken();
    const response = await apiClientEU.post(
      "/companionFunctions-createConversation",
      { title, storyId, summary },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
    return response.data;
  } catch (error) {
    console.error("Error creating conversation:", error.response?.data || error.message);
    throw error.response?.data?.error || error.message;
  }
};

export const listConversations = async () => {
  try {
    const idToken = await getIdToken();
    const response = await apiClientEU.post(
      "/companionFunctions-listConversations",
      {},
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
    return response.data;
  } catch (error) {
    console.error("Error listing conversations:", error.response?.data || error.message);
    throw error.response?.data?.error || error.message;
  }
};

export const getMessages = async (conversationId) => {
  try {
    const idToken = await getIdToken();
    const response = await apiClientEU.post(
      "/companionFunctions-getMessages",
      { conversationId },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching messages for ${conversationId}:`, error.response?.data || error.message);
    throw error.response?.data?.error || error.message;
  }
};

export const sendMessage = async ({ conversationId, content }) => {
  try {
    const idToken = await getIdToken();
    const response = await apiClientEU.post(
      "/companionFunctions-sendMessage",
      { conversationId, content },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
    return response.data;
  } catch (error) {
    console.error(`Error sending message to ${conversationId}:`, error.response?.data || error.message);
    throw error.response?.data?.error || error.message;
  }
};

export const deleteConversation = async (conversationId) => {
  try {
    const idToken = await getIdToken();
    const response = await apiClientEU.post(
      "/companionFunctions-deleteConversation",
      { conversationId },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
    return response.data;
  } catch (error) {
    console.error(`Error deleting conversation ${conversationId}:`, error.response?.data || error.message);
    throw error.response?.data?.error || error.message;
  }
};

export { getIdToken, apiClient };
